import { map, size, get, chain, capitalize, toUpper, isEmpty } from "lodash"
import { VIEW_TYPES } from "helpers/base"
import { NORMALIZATION } from "helpers/segments"
import { AssetFilterOptions, FilterOption } from "resources/filters"
import { TagCategory } from "resources/tags"
import { Filter } from "connected-components/dashboard/context/types"
import { Classification, ClassificationMeta, ClassificationVersion, flattenClassificationValues } from "resources/classifications"
import { AttributeCategory } from "resources/attributes"
import { Normalization } from "connected-components/dashboard/helpers"
import { LONG_SHORT, INTRADAY_PRICING, INTRADAY_PRICING_VALUES } from "../filters"
import { STRING_OPERATORS } from "./filter-ons/strings"
import { NUMERIC_OPERATORS } from "./filter-ons/numbers"
import { NUMERIC_AND_EXISTENCE_OPERATORS } from "./filter-ons/existence"
import { CLASSIFICATION_OPERATORS } from "./filter-ons/classifications"
import { SECURITIES_OPERATORS } from "./filter-ons/securities"

type LONG_SHORT_TYPE = typeof LONG_SHORT
type INTRADAY_PRICING_TYPE = typeof INTRADAY_PRICING

function appendMembershipFilters(
  filtersArray: FilterOn[],
  filters: Record<string, FilterOption[]>,
  categories: AttributeCategory[] | TagCategory[] | null,
  isUnsupported: boolean,
  tooltip: string,
  type: "tag" | "positionAttributes",
  label: string,
) {
  const options = chain(categories)
    .map(({ id, name }): MembershipFilterOnOption | null => {
      if (size(filters[id]) === 0) {
        return null
      }
      return {
        label: name,
        value: id,
        dataType: "string",
        type,
        operators: STRING_OPERATORS,
        tooltip,
        isUnsupported,
        options: map(filters[id], option => ({
          ...option,
          type,
          dataType: "string",
        })),
      }
    })
    .compact()
    .value()

  if (size(options)) {
    filtersArray.push({
      label,
      type,
      tooltip,
      options,
      isUnsupported,
    })
  }
}

interface ClassificationMetaWithVersion extends Omit<ClassificationMeta, "versions"> {
  version: ClassificationVersion
}

interface FilterOnOption {
  type: string
  dataType: string
  label: string
  value: string
}

interface ConstantFilterOnOption extends FilterOnOption {
  type: LONG_SHORT_TYPE
  dataType: "constant"
}

interface StringDescriptorFilterOnOption extends FilterOnOption {
  type: "descriptor"
  dataType: "string"
  operators: typeof STRING_OPERATORS
  options: FilterOption[]
}

interface NumericDescriptorFilterOnOption extends FilterOnOption {
  type: "descriptor"
  dataType: "millions"
  operators: typeof NUMERIC_OPERATORS
}

interface ClassificationFilterOnOptionValue {
  value: string
  tierId: string
  label: string
}

export interface ClassificationFilterOnOption extends FilterOnOption {
  type: "classification"
  dataType: "string"
  operators: typeof CLASSIFICATION_OPERATORS
  options: ClassificationFilterOnOptionValue[]
  isDisabled?: boolean
}

interface SecurityFilterOnOption extends FilterOnOption {
  type: "securities"
  dataType: "string"
  operators: typeof SECURITIES_OPERATORS
}

export interface MembershipFilterOnOption extends FilterOnOption {
  type: "tag" | "positionAttributes"
  dataType: "string"
  operators: typeof STRING_OPERATORS
  // [TODO] Next 2 properties appear to be redundant with the parent
  isUnsupported: boolean
  tooltip: false | string
  options: {
    value: string
    label: string
    count: number
    type: string
    dataType: "string"
  }[]
}

export interface RiskFilterOnOption extends FilterOnOption {
  type: "risk"
  dataType: "percent"
  operators: typeof NUMERIC_OPERATORS
}

export interface BetaFilterOnOption extends FilterOnOption {
  type: "beta"
  operators: typeof NUMERIC_OPERATORS
  isDisabled?: boolean
}

interface FactorExposureFilterOnOption extends FilterOnOption {
  type: "factorExposure"
  operators: typeof NUMERIC_OPERATORS | typeof NUMERIC_AND_EXISTENCE_OPERATORS
  contentSetId: string
}

export interface RiskFilterOn {
  label: string
  type: "risk"
  options: RiskFilterOnOption[]
}

export interface BetaFilterOn {
  label: string
  type: "beta"
  options: BetaFilterOnOption[]
}

interface FactorExposureFilterOn {
  label: string
  type: "factorExposure"
  options: FactorExposureFilterOnOption[]
}

export type DescriptorFilterOnOption = StringDescriptorFilterOnOption | NumericDescriptorFilterOnOption

interface ConstantFilterOn {
  label: string
  type: LONG_SHORT_TYPE
  options: ConstantFilterOnOption[]
}

interface IntradayPricingFilterOnOption extends FilterOnOption {
  type: INTRADAY_PRICING_TYPE
  dataType: "constant"
  isUnsupported: boolean
  tooltip?: string
}

export interface IntradayPricingFilterOn {
  label: string
  type: INTRADAY_PRICING_TYPE
  isUnsupported: boolean
  tooltip?: string
  options: IntradayPricingFilterOnOption[]
}

export interface IntradayPricingFilterParams {
  isUnsupported: boolean
  tooltip?: string
}

interface DescriptorFilterOn {
  label: string
  type: "descriptor"
  options: DescriptorFilterOnOption[]
}

export interface ClassificationFilterOn {
  label: string
  type: "classification"
  options: ClassificationFilterOnOption[]
}

interface SecurityFilterOn {
  label: "Securities"
  type: "securities"
  options: SecurityFilterOnOption[]
}

export interface MembershipFilterOn {
  label: string
  type: "tag" | "positionAttributes"
  isUnsupported: boolean
  tooltip: string
  options: MembershipFilterOnOption[]
}

export type FilterOn = ConstantFilterOn | DescriptorFilterOn | ClassificationFilterOn | SecurityFilterOn | MembershipFilterOn | RiskFilterOn | BetaFilterOn | FactorExposureFilterOn | IntradayPricingFilterOn

export function computeFilterOns(
  // [TODO:TS] Refine
  viewType: "absolute" | "active",
  portfolioFilters: AssetFilterOptions,
  currentFilter: Filter | undefined,
  // [TODO:TS] Verify if can be removed
  editing: unknown,
  tagCategories: TagCategory[] | null,
  classifications: ClassificationMetaWithVersion[],
  classificationValues: Record<string, Classification> | null,
  attributeCategories: AttributeCategory[] | null,
  // [TODO:TS] Refine
  assetType: string,
  normalization: Normalization,
  hasMultipleInstruments: boolean,
  allowPositionAttributesFilter = false,
  intradayPricingFilter?: IntradayPricingFilterParams,
) {
  const filters: FilterOn[] = []
  if (!portfolioFilters) {
    return filters
  }

  const {
    long,
    short,
    countries,
    currencies,
    assetClasses,
    assetSubclasses,
    classifications: classificationFilters,
    tags: tagFilters,
    positionAttributes: attributeFilters,
  } = portfolioFilters

  if (long && short) {
    filters.push({
      label: "Long/Short",
      type: LONG_SHORT,
      options: [{
        label: "Long",
        type: LONG_SHORT,
        value: "LONG",
        dataType: "constant",
      }, {
        label: "Short",
        type: LONG_SHORT,
        value: "SHORT",
        dataType: "constant",
      }],
    })
  }

  if (intradayPricingFilter) {
    const { isUnsupported, tooltip } = intradayPricingFilter
    filters.push({
      label: "Intraday Pricing",
      type: INTRADAY_PRICING,
      isUnsupported,
      tooltip,
      options: [{
        label: "Priced Intraday",
        type: INTRADAY_PRICING,
        value: INTRADAY_PRICING_VALUES.PRICED,
        dataType: "constant",
        isUnsupported,
        tooltip,
      }, {
        label: "Not Priced Intraday",
        type: INTRADAY_PRICING,
        value: INTRADAY_PRICING_VALUES.UNPRICED,
        dataType: "constant",
        isUnsupported,
        tooltip,
      }],
    })
  }

  const descriptorsOptions: DescriptorFilterOnOption[] = []

  if (size(countries) > 1) {
    descriptorsOptions.push({
      label: "Country",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "country",
      options: countries,
    })
  }

  if (size(currencies) > 1) {
    descriptorsOptions.push({
      label: "Currency",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "currency",
      options: currencies,
    })
  }

  if (size(assetClasses) > 1) {
    descriptorsOptions.push({
      label: "Asset Class",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "assetClass",
      options: assetClasses,
    })
  }

  if (size(assetSubclasses) > 1) {
    descriptorsOptions.push({
      label: "Asset Subclass",
      type: "descriptor",
      dataType: "string",
      operators: STRING_OPERATORS,
      value: "assetSubclass",
      options: assetSubclasses,
    })
  }

  descriptorsOptions.push({
    label: "Average Daily Volume",
    type: "descriptor",
    dataType: "millions",
    operators: NUMERIC_OPERATORS,
    value: "averageDailyVolume",
  }, {
    label: "Market Cap",
    type: "descriptor",
    dataType: "millions",
    operators: NUMERIC_OPERATORS,
    value: "marketCapitalization",
  })

  filters.push({
    label: "Descriptors",
    type: "descriptor",
    options: descriptorsOptions,
  })

  if (size(classificationFilters) > 0 && size(classifications) > 0) {
    const options: ClassificationFilterOnOption[] = []

    for (const classification of classifications) {
      const versionValues = get(classificationValues, [classification.id, "version", "values"])
      const tiers = classification.version.tiers
      const allClassificationValues = flattenClassificationValues(tiers, versionValues, 1)
      options.push({
        label: `${classification.name}`,
        type: "classification",
        dataType: "string",
        operators: CLASSIFICATION_OPERATORS,
        options: map(classificationFilters[classification.id], v => ({
          value: v.value,
          tierId: chain(allClassificationValues).find(a => a.value === v.value).get("tierId").value(),
          label: `${v.label} - ${v.value}`,
        })),
        value: classification.id,
      })
    }

    filters.push({
      label: "Classification",
      type: "classification",
      options,
    })
  }

  filters.push({
    label: "Securities",
    type: "securities",
    options: [{
      label: `${toUpper(assetType) === "ETF" ? "ETF" : (capitalize(assetType) || "Portfolio")} Securities`,
      type: "securities",
      dataType: "string",
      operators: SECURITIES_OPERATORS,
      value: "securities",
    }],
  })

  const isTagFilterSupported = viewType !== VIEW_TYPES.ACTIVE || normalization === NORMALIZATION.STANDALONE
  const isUnsupported = !isTagFilterSupported || hasMultipleInstruments

  if (!isEmpty(tagFilters) && !isEmpty(tagCategories)) {
    const tooltip = hasMultipleInstruments ? "Filtering on tags is not allowed in dashboard with multiple instruments." : "Filtering on tags is not allowed when active."
    appendMembershipFilters(
      filters,
      tagFilters,
      tagCategories,
      isUnsupported,
      tooltip,
      "tag",
      "Tags",
    )
  }

  if (!isEmpty(attributeFilters) && !isEmpty(attributeCategories)) {
    // Unsupported only when both isUnsupported=true and allowPositionAttributesFilter=false
    // This allows allowPositionAttributesFilter to override and enable filters even when isUnsupported=true
    const isPositionAttributesUnsupported = isUnsupported && !allowPositionAttributesFilter
    const tooltip = hasMultipleInstruments ? "Filtering on position set attributes is not allowed in dashboard with multiple instruments." : "Filtering on position set attributes is not allowed when active."
    appendMembershipFilters(
      filters,
      attributeFilters,
      attributeCategories,
      isPositionAttributesUnsupported,
      tooltip,
      "positionAttributes",
      "Position Attributes",
    )
  }

  return filters
}
